import { z } from 'zod'

// Grounded against fd-client-api-google-ads source (not the FE migration-guide
// docs, which are stale in places):
//   app/api/v4/setup_process.py            — setup_process_serializer
//   app/core/campaign/schemas/targeting.py — TC_TARGETING_ITEM_SCHEMA, FB_TC_TARGETING_SCHEMA, GOOGLE_TC_TARGETING_SCHEMA
//   app/core/campaign/schemas/creative.py  — CREATIVE_SCHEMA, MEDIA_ITEM_SCHEMA
//   app/core/campaign/schemas/platform_overrides.py

export const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')

const ageRangeSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
  })
  .passthrough()

// additionalProperties is intentionally left open on both platform sub-objects
// below (matches the backend's own schemas — Meta/Google API surfaces evolve).
const fbTargetingSchema = z
  .object({
    targeting_mode: z.enum(['fd_targeting', 'postal_codes', 'range_locations']).optional(),
    age_range: ageRangeSchema.optional(),
    range_locations: z.object({}).passthrough().optional(),
    postal_codes: z.object({}).passthrough().optional(),
  })
  .passthrough()

const googleTargetingSchema = z
  .object({
    targeting_mode: z.enum(['fd_targeting', 'postal_codes', 'range_locations']).optional(),
    age_range: ageRangeSchema.optional(),
    range_locations: z.object({}).passthrough().optional(),
    postal_codes: z.object({}).passthrough().optional(),
    age_ranges: z.array(z.string()).optional(),
    genders: z.array(z.string()).optional(),
    income_ranges: z.array(z.string()).optional(),
    parental_statuses: z.array(z.string()).optional(),
    languages: z.array(z.string()).optional(),
    audiences: z.array(z.unknown()).optional(),
    excluded_audiences: z.array(z.unknown()).optional(),
    audience_resource_names: z.array(z.string()).optional(),
    interest_segment_ids: z.array(z.string()).optional(),
    topic_resource_names: z.array(z.string()).optional(),
    placement_urls: z.array(z.string()).optional(),
    excluded_placement_urls: z.array(z.string()).optional(),
    youtube_video_ids: z.array(z.string()).optional(),
    youtube_channel_ids: z.array(z.string()).optional(),
    keywords: z.array(z.unknown()).optional(),
    excluded_keywords: z.array(z.unknown()).optional(),
    life_event_resource_names: z.array(z.string()).optional(),
  })
  .passthrough()

// Platform-nested per-TC targeting entry: {tc, tc_run_id, fb?, google?} — the
// backend rejects any other top-level key (additionalProperties: false), but
// we parse leniently here since this side only ever reads what the backend sent.
export const targetingEntrySchema = z
  .object({
    tc: z.string(),
    tc_run_id: z.number(),
    fb: fbTargetingSchema.optional(),
    google: googleTargetingSchema.optional(),
  })
  .passthrough()

const mediaItemSchema = z
  .object({
    id: z.string().optional(),
    url: z.string().optional(),
    youtube_video_id: z.string().nullable().optional(),
    size: z.number().optional(),
    type: z.string().optional(),
    index: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    status: z.string().optional(),
    filename: z.string().optional(),
    placement_group_id: z.string().nullable().optional(),
  })
  .passthrough()

const displayUrlPathSchema = z
  .object({
    url: z.string(),
    path1: z.string().optional(),
    path2: z.string().optional(),
  })
  .passthrough()

// Exported for reuse by domain/publishedCampaignSetups — a published TC's
// creative (taste_clusters[].creative) is the same unified shape as a
// pre-publish setup_process creative, confirmed against
// event_campaign_service.py's edit_campaign_setup (writes the request's
// `creative` straight onto event_campaign.creatives via merge_creatives,
// no per-platform reshaping).
// call_to_action is polymorphic: a {fb, google} object always works
// (including single-platform, e.g. {fb: "BOOK_NOW"} alone — confirmed with
// BE), or a plain string, which only ever appears as legacy data predating
// the Google CTA feature. Reads must handle both; CampaignForm.jsx always
// writes the object form. See CampaignForm.jsx for the read/write logic
// that derives per-platform values from this and rebuilds it on save.
const callToActionSchema = z.union([
  z.string(),
  z.object({ fb: z.string().optional(), google: z.string().optional() }).passthrough(),
])

export const creativeSchema = z
  .object({
    tc: z.string(),
    tc_run_id: z.number(),
    body: z.array(z.string()).optional(),
    headline: z.array(z.string()).optional(),
    long_headline: z.array(z.string()).optional(),
    description: z.string().nullable().optional(),
    language: z.string().optional(),
    default_locale: z.string().optional(),
    call_to_action: callToActionSchema.optional(),
    destination_url: z.array(z.string()).optional(),
    display_url_path: z.array(displayUrlPathSchema).optional(),
    media: z.array(mediaItemSchema).optional(),
    placement_media: z.array(mediaItemSchema).optional(),
    excluded_variations: z.array(z.string()).optional(),
    localizations: z.record(z.string(), z.unknown()).optional(),
    tracking_template: z.string().optional(),
    companion_banner: mediaItemSchema.nullable().optional(),
    logo: mediaItemSchema.nullable().optional(),
    business_name: z.string().optional(),
    carousel_cards: z.array(z.unknown()).optional(),
    carousel_options: z.unknown().optional(),
  })
  .passthrough()

const googleCampaignTargetingSchema = z
  .object({
    targeting_mode: z.enum(['fd_targeting', 'postal_codes', 'range_locations']).optional(),
    range_locations: z.object({}).passthrough().optional(),
    postal_codes: z.object({}).passthrough().optional(),
    languages: z.array(z.string()).optional(),
    devices: z.array(z.string()).optional(),
    content_label_exclusions: z.array(z.string()).optional(),
  })
  .passthrough()

const fbCampaignTargetingSchema = z
  .object({
    location_recency: z.array(z.string()).optional(),
  })
  .passthrough()

// Campaign-wide targeting (applies to every TC), keyed by platform — same
// two keys as per-TC targeting but a narrower field set. See
// app/core/campaign/schemas/targeting.py's CAMPAIGN_TARGETING_SCHEMA.
export const campaignTargetingSchema = z
  .object({
    google: googleCampaignTargetingSchema.optional(),
    fb: fbCampaignTargetingSchema.optional(),
  })
  .passthrough()

const BIDDING_STRATEGY_TYPES = [
  'MANUAL_CPM',
  'MAXIMIZE_CLICKS',
  'MAXIMIZE_CONVERSIONS',
  'MAXIMIZE_CONVERSION_VALUE',
  'TARGET_SPEND',
  'TARGET_CPA',
  'TARGET_ROAS',
]

// Shorthand string, or a rich object when target params are needed. See
// app/core/campaign/schemas/platform_overrides.py's PLATFORM_OVERRIDES_SCHEMA.
const biddingStrategySchema = z.union([
  z.enum(BIDDING_STRATEGY_TYPES),
  z
    .object({
      type: z.enum(BIDDING_STRATEGY_TYPES),
      target_cpa_micros: z.number().optional(),
      target_roas: z.number().optional(),
      cpc_bid_ceiling_micros: z.number().optional(),
    })
    .passthrough(),
])

const googlePlatformOverridesSchema = z
  .object({
    campaign_type: z.enum(['DISPLAY', 'VIDEO_CONVERSIONS']).optional(),
    bidding_strategy: biddingStrategySchema.optional(),
    // Viewable CPM bid for this ad group, in micros — only meaningful when
    // bidding_strategy resolves to a CPM-type strategy (MANUAL_CPM).
    cpm_bid_micros: z.number().optional(),
    tracking_url_template: z.string().optional(),
    url_custom_parameters: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
    // Demand Gen only.
    brand_guidelines_enabled: z.boolean().optional(),
    brand_guidelines: z
      .object({
        main_color: z.string().optional(),
        accent_color: z.string().optional(),
        predefined_font_family: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

// Only "google" is a valid top-level key today (backend rejects others with
// additionalProperties: false on write) — kept as passthrough here since
// this side only ever reads what a real response contains.
export const platformOverridesSchema = z
  .object({
    google: googlePlatformOverridesSchema.optional(),
  })
  .passthrough()

// Inline integration_details entry. `id`/`partner_id` are absent for the
// conversion/lead_form/custom_event_type entries (inline references, not
// PartnerIntegration rows) — see migration guide's integration_details section.
export const integrationDetailSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    partner_id: z.string().optional(),
    platform: z.string(),
    asset_class: z.string(),
    asset_id: z.string(),
    asset_label: z.string().optional(),
  })
  .passthrough()

export const setupPlatformFieldSchema = z.array(z.enum(['facebook', 'google']))

export const setupProcessResponseSchema = z
  .object({
    id: z.number(),
    goal: z.string().optional(),
    total_budget: z.number().optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    platform: setupPlatformFieldSchema.optional(),
    creatives: z.array(creativeSchema).optional(),
    targeting: z.array(targetingEntrySchema).optional(),
    campaign_targeting: campaignTargetingSchema.optional(),
    platform_overrides: platformOverridesSchema.optional(),
    dsa_beneficiary: z.string().nullable().optional(),
    dsa_payor: z.string().nullable().optional(),
    integration_details: z.array(integrationDetailSchema).optional(),
  })
  .passthrough()

export const setupProcessPreviewResponseSchema = z
  .object({
    budget_in_ad_account_currency: z.number().nullable().optional(),
  })
  .passthrough()

export const setupProcessRequestSchema = z
  .object({
    goal: z.string().optional(),
    total_budget: z.number().nullable().optional(),
    start_date: dateOnlySchema.nullable().optional(),
    end_date: dateOnlySchema.nullable().optional(),
    platform: setupPlatformFieldSchema.optional(),
    creatives: z.array(creativeSchema).optional(),
    targeting: z.array(targetingEntrySchema).optional(),
    campaign_targeting: campaignTargetingSchema.optional(),
    platform_overrides: platformOverridesSchema.optional(),
    integration_details: z.array(integrationDetailSchema).optional(),
    dsa_payor: z.string().nullable().optional(),
    dsa_beneficiary: z.string().nullable().optional(),
  })
  .passthrough()
